<template>
  <b-card no-body class="mb-4">
    <template #header>
      <div class="d-flex align-items-center justify-content-between">
        <span><i class="fas fa-rectangle-ad mr-2"></i>จัดการโฆษณา</span>
        <div>
          <b-button size="sm" variant="outline-secondary" class="mr-2" @click="load">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          </b-button>
          <b-button size="sm" variant="primary" @click="openEditor(null)">
            <i class="fas fa-plus mr-1"></i> สร้างโฆษณา
          </b-button>
        </div>
      </div>
    </template>

    <b-alert v-if="error" variant="danger" show class="m-3">{{ error }}</b-alert>

    <b-table
      :items="ads"
      :fields="fields"
      :busy="loading"
      responsive
      hover
      show-empty
      empty-text="ยังไม่มีโฆษณาในระบบ"
      class="mb-0"
    >
      <template #cell(status)="row">
        <b-badge :variant="statusOf(row.item).variant">
          {{ statusOf(row.item).label }}
        </b-badge>
      </template>

      <template #cell(placement)="row">
        <b-badge v-if="row.item.showInCarousel" variant="light" class="mr-1">
          แถบโฆษณา
        </b-badge>
        <b-badge v-if="row.item.showInPopup" variant="light">ป๊อปอัป</b-badge>
      </template>

      <template #cell(schedule)="row">
        <span v-if="row.item.startDt || row.item.endDt" class="small">
          {{ formatDate(row.item.startDt) || '—' }} →
          {{ formatDate(row.item.endDt) || '—' }}
        </span>
        <span v-else class="text-muted small">ตลอดเวลา</span>
      </template>

      <template #cell(stats)="row">
        <span v-if="statsFor(row.item)" class="small">
          <span :title="'จำนวนครั้งที่แสดง (' + rangeLabel + ')'">
            <i class="fas fa-eye text-muted mr-1"></i>{{ statsFor(row.item).impressions.toLocaleString() }}
          </span>
          <span class="ml-2" :title="'ผู้ใช้ที่เห็น (นับซ้ำได้รายวัน)'">
            <i class="fas fa-users text-muted mr-1"></i>{{ statsFor(row.item).dailyReach.toLocaleString() }}
          </span>
          <span class="ml-2" :title="'จำนวนคลิก'">
            <i class="fas fa-hand-pointer text-muted mr-1"></i>{{ statsFor(row.item).clicks.toLocaleString() }}
          </span>
        </span>
        <span v-else class="text-muted small">—</span>
      </template>

      <template #cell(actions)="row">
        <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEditor(row.item)">
          <i class="fas fa-pen"></i>
        </b-button>
        <b-button size="sm" variant="outline-danger" @click="confirmDelete(row.item)">
          <i class="fas fa-trash"></i>
        </b-button>
      </template>
    </b-table>

    <AdsEditorModal ref="editor" @saved="onSaved" />
  </b-card>
</template>

<script>
import AdsApiService from '@/services/AdsApiService';
import AdsEditorModal from '@/components/superadmin/AdsEditorModal.vue';

export default {
  name: 'AdsManagerTable',
  components: { AdsEditorModal },
  props: {
    /**
     * Report rows for the last 30 days, keyed by ad, so each row can show how
     * it is actually performing. Optional: the table is useful without them.
     */
    stats: {
      type: Array,
      default: () => []
    },
    rangeLabel: {
      type: String,
      default: '30 วันล่าสุด'
    }
  },
  data() {
    return {
      ads: [],
      loading: false,
      error: null,
      fields: [
        { key: 'title', label: 'ชื่อโฆษณา' },
        { key: 'type', label: 'ประเภท' },
        { key: 'priority', label: 'ลำดับ' },
        { key: 'placement', label: 'ตำแหน่ง' },
        { key: 'schedule', label: 'ช่วงเวลา' },
        { key: 'status', label: 'สถานะ' },
        // Props are available in data(); computed properties are not.
        { key: 'stats', label: this.rangeLabel },
        { key: 'actions', label: '' }
      ]
    };
  },
  computed: {
    statsByAdId() {
      const map = new Map();
      for (const row of this.stats) map.set(row.adId, row);
      return map;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const response = await AdsApiService.listAds();
        this.ads = response?.data?.data?.ads || [];
      } catch (error) {
        this.error =
          error?.response?.data?.message || 'โหลดรายการโฆษณาไม่สำเร็จ';
      } finally {
        this.loading = false;
      }
    },

    statsFor(ad) {
      return this.statsByAdId.get(ad.id) || null;
    },

    /**
     * "Active" alone would lie: an ad can be switched on and still be invisible
     * because its schedule has not started or has already ended.
     */
    statusOf(ad) {
      if (!ad.isActive) return { label: 'ปิดใช้งาน', variant: 'secondary' };

      const now = Date.now();
      if (ad.startDt && new Date(ad.startDt).getTime() > now) {
        return { label: 'รอเริ่ม', variant: 'info' };
      }
      if (ad.endDt && new Date(ad.endDt).getTime() < now) {
        return { label: 'หมดอายุ', variant: 'warning' };
      }
      return { label: 'กำลังแสดง', variant: 'success' };
    },

    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString('th-TH');
    },

    openEditor(ad) {
      this.$refs.editor.show(ad);
    },

    onSaved() {
      this.load();
      this.$emit('changed');
    },

    async confirmDelete(ad) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `ต้องการเลิกใช้โฆษณา "${ad.title || ad.id}" ใช่หรือไม่? ข้อมูลสถิติที่เก็บไว้จะยังอยู่`,
        {
          title: 'ยืนยันการลบ',
          okVariant: 'danger',
          okTitle: 'เลิกใช้',
          cancelTitle: 'ยกเลิก',
          centered: true
        }
      );
      if (!confirmed) return;

      try {
        await AdsApiService.deleteAd(ad.id);
        this.load();
        this.$emit('changed');
      } catch (error) {
        this.error = error?.response?.data?.message || 'ลบโฆษณาไม่สำเร็จ';
      }
    }
  }
};
</script>
